import React, { useState } from 'react';
import Button from '../../../../components/button/Button';
import CustomTable from '../../../../components/CustomTable';
import {
    CircleDeleteIcon,
    LinkIcon,
    MoreIcon,
    RefreshCircleIcon
} from '../../../../components/icons';
import { formatDate, formatNumber } from '../../../../utils/helper';
import { Avatar, Dropdown, MenuProps, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTreasuryAccounts } from '../../../../customhooks/treasury/useTreasuryAccounts';
import { Bank } from '../../../../customhooks/useBankLogos';
import { TreasuryAccount } from '../../../../interfaces/Treasury';
import { UnlinkAccount } from '../TransactionsDetails/Components/UnlinkModal';
import TreasuryAccountMobileCard from './TreasuryAccountMobileCard';
import ConnectBankModal from '../ConnectBank';
import FilterAccounts from './FilterAccounts';
import { useUserProfile } from '../../../../customhooks/useUserProfile';
import { hasPermission } from '../../../../components/dashboard/dashboardpage/utils';
import { getBanks } from 'banks-ng';

const TreasuryOverviewAccountsTable = ({
    pageView = false
}: {
    pageView?: boolean;
}) => {
    const navigate = useNavigate();
    const [filters, setFilters] = useState<{ accountNumber?: string }>({
        accountNumber: undefined
    });
    const { permissions } = useUserProfile();
    const {
        accounts,
        isLoadingAccounts,
        refetchAccounts,
        isFetching
    } = useTreasuryAccounts({ params: filters });

    const [selected, setSelected] = useState<TreasuryAccount | null>(null);
    const [openModal, setOpenModal] = useState('');

    const handleViewTransactions = (record: TreasuryAccount) => {
        record.connectStatus === 'ACTIVE'
            ? navigate(`/treasury/accounts/${String(record.id)}`)
            : null;
    };

    const getMenu = (record: TreasuryAccount): MenuProps['items'] => [
        {
            key: 'unlink',
            label: (
                <div className='flex items-center h-[30px] w-[120px] gap-2 text-[#E04F16] text-sm'>
                    <CircleDeleteIcon color='#E04F16' />
                    <span>Unlink account</span>
                </div>
            ),
            onClick: () => {
                setSelected(record);
                setOpenModal('unlink');
            },
            className: 'no-click'
        },
        {
            key: 'refresh',
            label: (
                <div className='flex items-center h-[30px] w-[120px] gap-2 text-steelBlue text-sm'>
                    <RefreshCircleIcon />
                    <span>Refresh</span>
                </div>
            ),
            onClick: () => refetchAccounts(),
            className: 'no-click'
        }
    ];

    const BankResult: Bank[] = getBanks();

    const bankLookupByName = React.useMemo(() => {
        const map = new Map<string, Bank>();
        BankResult.forEach((bank: Bank) => {
            map.set(bank.name.toLowerCase(), bank);
        });
        return map;
    }, [BankResult]);

    return (
        <div className='bg-white rounded-[8px]'>
            <div className='flex justify-between items-center px-[16px] py-[20px]'>
                <h4 className='hidden lg:block text-ebony font-semibold text-lg'>
                    All accounts
                </h4>

                <div className='hidden lg:flex gap-2'>
                    {!pageView ? (
                        permissions &&
                        hasPermission({
                            userPermissions: permissions,
                            module: 'treasuryManagement',
                            permissionList: ['link'],
                            feature: 'treasuryManagement'
                        }) && (
                            <Button
                                leftIcon={<LinkIcon />}
                                text='Link a bank account'
                                className='font-medium'
                                onClick={() => setOpenModal('connect')}
                                size='sm'
                            />
                        )
                    ) : (
                        <>
                            <FilterAccounts {...{ filters, setFilters }} />
                            <Button
                                leftIcon={<RefreshCircleIcon />}
                                text='Refresh Account'
                                className='font-medium text-ebony'
                                onClick={refetchAccounts}
                                size='sm'
                                variant='tertiary'
                            />
                        </>
                    )}
                </div>
            </div>

            <ConnectBankModal
                open={openModal === 'connect'}
                onClose={() => setOpenModal('')}
            />

            <CustomTable
                data={accounts ?? []}
                loadingData={isLoadingAccounts || isFetching}
                columns={[
                    {
                        title: 'Connections',
                        dataIndex: 'name',
                        key: 'name',
                        render: (text: string) => {
                            const bank = bankLookupByName.get(
                                text.toLowerCase()
                            );

                            return (
                                <div className='flex gap-3 items-center'>
                                    <Avatar
                                        size='default'
                                        src={bank?.logo}
                                        style={{
                                            border: '1px solid #D0D5DD',
                                            background: 'transparent'
                                        }}
                                    />
                                    <p>{text}</p>
                                </div>
                            );
                        }
                    },
                    {
                        title: 'Account name',
                        dataIndex: 'accountName',
                        key: 'accountName'
                    },
                    {
                        title: 'Number',
                        dataIndex: 'accountNumber',
                        key: 'accountNumber'
                    },
                    {
                        title: 'Balance',
                        dataIndex: 'accountBalance',
                        key: 'accountBalance',
                        render: (_, { connectStatus, accountBalance }) => (
                            <>
                                {connectStatus === 'PENDING' ? (
                                    <span>N/A</span>
                                ) : (
                                    <span>
                                        NGN {formatNumber(accountBalance)}
                                    </span>
                                )}
                            </>
                        )
                    },
                    {
                        title: 'Connected at',
                        dataIndex: 'createdDate',
                        key: 'createdDate',
                        render: (_, { createdDate }) => (
                            <span>{formatDate(createdDate)}</span>
                        )
                    },
                    {
                        title: 'Status',
                        dataIndex: 'connectStatus',
                        key: 'connectStatus',
                        render: (_, { connectStatus }) => (
                            <Tag
                                bordered={false}
                                color={
                                    connectStatus === 'INACTIVE'
                                        ? 'default'
                                        : connectStatus === 'ACTIVE'
                                        ? 'success'
                                        : connectStatus === 'UNAUTHORIZED'
                                        ? 'error'
                                        : 'warning'
                                }
                            >
                                {connectStatus}
                            </Tag>
                        )
                    },
                    {
                        title: 'Action',
                        render: (_, record) =>
                            record.connectStatus === 'ACTIVE' ? (
                                <Dropdown
                                    menu={{
                                        items: getMenu(record)
                                    }}
                                    trigger={['click']}
                                >
                                    <div className='rotate-90 flex flex-row justify-center cursor-pointer'>
                                        <MoreIcon color='#98A2B3' />
                                    </div>
                                </Dropdown>
                            ) : (
                                <div className='rotate-90 opacity-60 flex flex-row justify-center cursor-not-allowed'>
                                    <MoreIcon color='#98A2B3' />
                                </div>
                            ),
                        className: 'no-click',
                        align: 'center'
                    }
                ]}
                showPagination={false}
                updateCurrentData={(record) => handleViewTransactions(record)}
                onMobileRowClick={(record) => handleViewTransactions(record)}
                mobileComponent={
                    <TreasuryAccountMobileCard getMenu={getMenu} />
                }
            />

            <UnlinkAccount
                open={openModal === 'unlink'}
                onClose={() => setOpenModal('')}
                selected={selected}
            />
        </div>
    );
};

export default TreasuryOverviewAccountsTable;
